import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
  resendVerification: () => api.post('/auth/resend-verification'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
};

// Empresas API
export const empresasApi = {
  getAll: () => api.get('/empresas'),
  getOne: (id) => api.get(`/empresas/${id}`),
  update: (id, data) => api.put(`/empresas/${id}`, data),
  getUsuarios: (empresaId) => api.get(`/empresas/${empresaId}/usuarios`),
  addUsuario: (empresaId, email, rol) => api.post(`/empresas/${empresaId}/usuarios`, null, { params: { email, rol } }),
  removeUsuario: (empresaId, usuarioId) => api.delete(`/empresas/${empresaId}/usuarios/${usuarioId}`),
  getLimites: (empresaId) => api.get(`/empresas/${empresaId}/limites`),
  getRecordatoriosConfig: (empresaId) => api.get(`/empresas/${empresaId}/recordatorios/config`),
  updateRecordatoriosConfig: (empresaId, config) => api.put(`/empresas/${empresaId}/recordatorios/config`, config),
  enviarRecordatorios: (empresaId) => api.post(`/empresas/${empresaId}/recordatorios/enviar`),
  uploadLogo: (empresaId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/empresas/${empresaId}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteLogo: (empresaId) => api.delete(`/empresas/${empresaId}/logo`),
};

// Clientes API
export const clientesApi = {
  getAll: (empresaId) => api.get('/clientes', { params: { empresa_id: empresaId } }),
  getOne: (id) => api.get(`/clientes/${id}`),
  create: (data) => api.post('/clientes', data),
  update: (id, data) => api.put(`/clientes/${id}`, data),
  delete: (id) => api.delete(`/clientes/${id}`),
};

// Presupuestos API
export const presupuestosApi = {
  getAll: (empresaId, estado) => api.get('/presupuestos', { params: { empresa_id: empresaId, estado } }),
  getOne: (id) => api.get(`/presupuestos/${id}`),
  create: (data) => api.post('/presupuestos', data),
  update: (id, data) => api.put(`/presupuestos/${id}`, data),
  delete: (id) => api.delete(`/presupuestos/${id}`),
  enviar: (id) => api.post(`/presupuestos/${id}/enviar`),
  duplicar: (id) => api.post(`/presupuestos/${id}/duplicar`),
  exportPdf: (id) => api.get(`/presupuestos/${id}/pdf`, { responseType: 'blob' }),
  crearRevision: (id) => api.post(`/presupuestos/${id}/revision`),
  getRevisiones: (id) => api.get(`/presupuestos/${id}/revisiones`),
  getPorVencer: (empresaId, dias = 7) => api.get(`/empresas/${empresaId}/presupuestos/por-vencer`, { params: { dias } }),
  // Adjuntos
  getAdjuntos: (id) => api.get(`/presupuestos/${id}/adjuntos`),
  uploadAdjunto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/presupuestos/${id}/adjuntos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAdjunto: (id, adjuntoId) => api.delete(`/presupuestos/${id}/adjuntos/${adjuntoId}`),
};

// Series API
export const seriesApi = {
  getAll: (empresaId) => api.get('/series', { params: { empresa_id: empresaId } }),
  create: (data) => api.post('/series', data),
  update: (id, data) => api.put(`/series/${id}`, data),
  delete: (id) => api.delete(`/series/${id}`),
};

// Plantillas API
export const plantillasApi = {
  getAll: (empresaId) => api.get('/plantillas', { params: { empresa_id: empresaId } }),
  getOne: (id) => api.get(`/plantillas/${id}`),
  create: (data) => api.post('/plantillas', data),
  update: (id, data) => api.put(`/plantillas/${id}`, data),
  delete: (id) => api.delete(`/plantillas/${id}`),
};

// Dashboard API
export const dashboardApi = {
  getMetricas: (empresaId) => api.get('/dashboard/metricas', { params: { empresa_id: empresaId } }),
};

// Email Tracking API
export const emailTrackingApi = {
  getTracking: (presupuestoId) => api.get(`/email/tracking/${presupuestoId}`),
};

// Push Notifications API
export const pushApi = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: (endpoint) => api.delete('/push/unsubscribe', { params: { endpoint } }),
  getSubscriptions: () => api.get('/push/subscriptions'),
  sendTest: () => api.post('/push/send-test'),
};

// Export CSV API
export const exportApi = {
  presupuestos: (empresaId, params = {}) => api.get('/export/presupuestos', { 
    params: { empresa_id: empresaId, ...params },
    responseType: 'blob'
  }),
  clientes: (empresaId) => api.get('/export/clientes', { 
    params: { empresa_id: empresaId },
    responseType: 'blob'
  }),
  itemsPresupuesto: (presupuestoId) => api.get(`/export/items-presupuesto/${presupuestoId}`, {
    responseType: 'blob'
  }),
};

// Public API (no auth)
export const publicApi = {
  getPresupuesto: (enlace) => axios.get(`${API_URL}/public/presupuesto/${enlace}`),
  responder: (enlace, accion, comentario) => axios.post(`${API_URL}/public/presupuesto/${enlace}/responder`, null, { params: { accion, comentario } }),
  exportPdf: (enlace) => axios.get(`${API_URL}/public/presupuesto/${enlace}/pdf`, { responseType: 'blob' }),
  getAdjuntos: (enlace) => axios.get(`${API_URL}/public/presupuesto/${enlace}/adjuntos`),
};

// Planes API
export const planesApi = {
  getAll: () => api.get('/planes'),
};

// Seed API
export const seedApi = {
  seed: () => api.post('/seed'),
};

export default api;
